import { Link } from 'react-router'

import type { RecordingRecord } from '../../lib/api'
import { Icon, type IconName } from '../ui'
import {
  CAFFEINE_LAST_LABELS,
  INTENSITY_LABELS,
  NUMERIC_FIELDS,
  QUALITY_LABELS,
  STATE_OPTIONS,
  TAG_LABELS,
  WHEN_AGO_LABELS,
  hasAnswer as present,
  numericText,
  provenanceWords,
} from '../../lib/selfReport'
import type { JournalTag } from '../../lib/selfReport'

/**
 * The session's own journal, read back — no form, no request, no chip.
 *
 * ── WHAT MAKES THIS DIFFERENT FROM THE FORM ─────────────────────────────────
 *
 * It reads `self_report` off the LIST ROW that Today already has, so the
 * screen makes no extra request for it. There is no live region and nothing to
 * save; editing is a link to the detail page, where the form lives (R8 keeps
 * the form's architecture, and O5 keeps a form off Today).
 *
 * ── `0` AND `false` ARE ANSWERS ─────────────────────────────────────────────
 *
 * Every part below is omitted only when its field is `null` or `undefined`. A
 * truthiness test would drop `caffeine_cups: 0` and `exercised: false`, which
 * are the two answers a person is most likely to have given deliberately. A
 * cell with no parts at all is omitted; the card itself is not.
 *
 * Labels and units are single-sourced from `lib/selfReport.ts` — the same
 * strings the form asks with, so the answer is read back under the question.
 * The five mood hints beside the form's options are a form affordance and are
 * not repeated here.
 *
 * Nothing here is rounded: `numericText` is `String(n)`, and a stored 7.53 is
 * shown as 7.53.
 */

/**
 * One `dt`/`dd` pair: the question, its label icon, and the parts of the answer.
 *
 * ── WHY THE ICONS ARE HERE AT ALL (Sprint 1.9.11) ───────────────────────────
 *
 * The concept gives the journal vocabulary one line icon per answer and says
 * they "label the values in the table and the form"; `JournalCells.tsx` draws
 * them in History's Journal view. This card reads the SAME answers back on the
 * session page, and it is the only place two of them are read back at all:
 * **water** never reaches the Journal table (owner ruling §5.3 keeps it here),
 * and the note is one of its cells.
 *
 * So the whole vocabulary is used, not one icon. A single `droplet` beside
 * seven bare values would read as a mark ON the water rather than a label of
 * it, which is the one thing the concept forbids these icons from doing. Each
 * icon below is the one `JournalCells` already uses for the same answer --
 * there is no second vocabulary, and Mood has none in either place.
 *
 * `Icon` is `aria-hidden` by construction and every icon sits beside a `<dt>`
 * that already asks the question in words, so nothing here is coloured,
 * ranked or dimmed by a value.
 */
interface Cell {
  label: string
  icon?: IconName
  parts: string[]
}

/**
 * THE ONE DEFINITION OF "A PART WITH NOTHING IN IT". Every caller below leans
 * on it; none repeats it.
 *
 * `undefined` is admitted BY TYPE and dropped at runtime, because a closed
 * `Record` lookup lies: `QUALITY_LABELS[q]` is typed `string`, and for a wire
 * value this build's enum does not carry it is `undefined`. The old predicate
 * said `part is string` while testing only `!== null`, so that `undefined`
 * survived a filter whose whole job was to remove it.
 *
 * Blank is dropped here for the same reason, and it is NOT the same case: an
 * empty string arrives on the wire under its own power. A note typed as spaces
 * is stored verbatim (`selfReport.ts:564` maps only the EXACTLY empty draft to
 * `null`, because trimming would rewrite the one free-text field a person wrote
 * in their own words), and `QUALITY_LABELS[''] ?? String('')` is `''`. Either
 * one joined into `'7 hours · '` -- a separator with nothing after it -- or,
 * alone, into an empty `<dd>`.
 *
 * `trim()` is the TEST, never the value: a part that has words is pushed with
 * its own spacing intact, so nothing a person actually wrote is rewritten.
 */
function push(
  cells: Cell[],
  label: string,
  parts: Array<string | null | undefined>,
  icon?: IconName,
) {
  const kept = parts.filter(
    (part): part is string => part !== null && part !== undefined && part.trim() !== '',
  )
  if (kept.length !== 0) cells.push({ label, icon, parts: kept })
}

export function JournalSummary({ recording, embedded = false }: { recording: RecordingRecord; embedded?: boolean }) {
  // `==` on purpose: a row that has never carried a journal has no such key at
  // all, and `undefined` is the same absence as `null`.
  const report = recording.self_report
  if (report == null) return null

  const words = provenanceWords(recording.self_report_provenance)

  /**
   * ── EVERY LABEL CARRIES THE FORM'S OWN TIME ANCHOR (Sprint 1.9.11) ──────
   *
   * Schema 2's whole change is that each question says WHEN it is about, and
   * the form asks these under "How you feel · right now", "Sleep · last
   * night", "Today, before this recording · since waking" and "Yesterday
   * evening and last night · last 24 h". Read back without those words,
   * "Exercised: Yes" does not say whether the exercise was before this
   * recording -- which is the defect BSV-011 records and the reason the anchors
   * exist.
   *
   * It is also why the old single "Food and drink" cell is three: caffeine and
   * water are since waking, alcohol is the last 24 h, and one label cannot
   * carry two anchors truthfully.
   */
  const cells: Cell[] = []
  push(cells, 'Mood, right now', [
    present(report.state)
      ? (STATE_OPTIONS.find((o) => o.value === report.state)?.label ?? String(report.state))
      : null,
  ])
  push(cells, 'Stress 1-10, right now', [
    present(report.stress_1_10) ? String(report.stress_1_10) : null,
  ], 'activity')
  push(cells, 'Sleep, last night', [
    present(report.sleep_hours)
      ? `${numericText(report.sleep_hours)} ${NUMERIC_FIELDS.sleep_hours.unit}`
      : null,
    // Raw, never dropped -- the rule the tag list below already follows. The
    // vocabulary is the backend's, and a quality this build has not seen is
    // still the person's own answer.
    present(report.sleep_quality)
      ? (QUALITY_LABELS[report.sleep_quality!] ?? String(report.sleep_quality))
      : null,
    // Stored as entered, in the words the form asks it with ("Woke up at").
    // Nothing here derives hours awake: that is the daily table's, server-side
    // (concept §A, owner ruling 2).
    present(report.woke_at_local) ? `woke ${report.woke_at_local}` : null,
  ], 'moon')
  push(cells, 'Exercised, since waking', [
    present(report.exercised) ? (report.exercised === true ? 'Yes' : 'No') : null,
    present(report.exercise_finished)
      ? `finished ${WHEN_AGO_LABELS[report.exercise_finished!] ?? String(report.exercise_finished)}`
      : null,
    present(report.exercise_intensity)
      ? (INTENSITY_LABELS[report.exercise_intensity!] ?? String(report.exercise_intensity))
      : null,
    present(report.exercise_minutes)
      ? `${numericText(report.exercise_minutes)} ${NUMERIC_FIELDS.exercise_minutes.unit}`
      : null,
  ], 'dumbbell')
  push(cells, 'Caffeine, since waking', [
    present(report.caffeine_cups)
      ? `${numericText(report.caffeine_cups)} ${NUMERIC_FIELDS.caffeine_cups.unit}`
      : null,
    present(report.caffeine_last)
      ? `last cup ${CAFFEINE_LAST_LABELS[report.caffeine_last!] ?? String(report.caffeine_last)}`
      : null,
  ], 'coffee')
  // Water is read back HERE and nowhere else: the Journal view in History
  // leaves it on the session page (owner ruling §5.3).
  push(cells, 'Water, since waking', [
    present(report.water_glasses)
      ? `${numericText(report.water_glasses)} ${NUMERIC_FIELDS.water_glasses.unit}`
      : null,
    // `droplet` labels an answer that appears on no other screen: History's
    // Journal view has no water column, by the owner's own ruling.
  ], 'droplet')
  push(cells, 'Alcohol, last 24 h', [
    present(report.alcohol_drinks)
      ? `${numericText(report.alcohol_drinks)} ${NUMERIC_FIELDS.alcohol_drinks.unit}`
      : null,
  ], 'wine')
  // No guard of its own: `push` owns emptiness. A second definition here is how
  // `''` came to be handled in one place and `'   '` in neither.
  push(cells, 'Notes', [report.notes], 'pen')

  const tags = report.tags ?? []

  const answers = <>
    {cells.length > 0 && (
      <dl className="detail-grid journal-summary">
        {cells.map((cell) => (
          <div className="detail-grid__item" key={cell.label}>
            <dt className="record__label">{cell.label}</dt>
            <dd className="record__value">
              {/* `journal-cell__phrase` / `journal-cell__icon`, the classes the
                  Journal view's own phrases use -- the same 14px muted glyph
                  beside the same answer, and no new CSS to keep in step. A
                  `<span>` is enough here because every part is text. */}
              <span className="journal-cell__phrase">
                {cell.icon !== undefined && (
                  <Icon name={cell.icon} className="journal-cell__icon" />
                )}
                {cell.parts.join(' · ')}
              </span>
            </dd>
          </div>
        ))}
      </dl>
    )}
    {tags.length > 0 && (
      /* A `<div>` and not a `<span>`: the value this icon labels IS a `<ul>`,
         and a list inside a `<span>` is not valid HTML. */
      <div className="journal-cell__phrase">
        <Icon name="tag" className="journal-cell__icon" />
        <ul className="journal-summary__tags">
          {tags.map((tag) => <li className="tag" key={tag}>{TAG_LABELS[tag as JournalTag] ?? tag}</li>)}
        </ul>
      </div>
    )}
  </>

  if (embedded) return <div className="journal-summary--embedded">
    {words !== null && <p className="prov">{words.origin}{words.edited !== null ? ` · ${words.edited}` : ''}</p>}
    {answers}
  </div>

  return (
    <section className="card journal-summary-card" aria-label="This session’s journal">
      <div className="card__head">
        <div className="card__eyebrow">This session’s journal</div>
        {/* When `provenanceWords` answers null there is NO claim available to
            make about where this came from, so nothing is said — the rule
            `History.tsx`'s cell already follows. */}
        {words !== null && (
          <span className="prov">
            {words.origin}
            {words.edited !== null ? ` · ${words.edited}` : ''}
          </span>
        )}
      </div>

      {answers}

      <p>Your own account of the day. Nothing here is interpreted, and it never changes your result.</p>

      <Link className="btn btn--ghost btn--sm" to={`/history/${recording.recording_id}`}>
        Edit in History
      </Link>
    </section>
  )
}
